import { useMutation, useQuery } from "@apollo/client";
import { useEffect } from "react";

import { useFormik } from "formik";
import {
  GridInputs,
  Table,
  multiSelect,
} from "../../../../../../../components/Functional";
import { Button, Modal } from "../../../../../../../components/ui";
import * as Yup from "yup";
import { FETCH_WAREHOUSE_ITEMS_V2 as fetchWarehouseItemsV2 } from "../../../../../../../graphql/query/WarehouseItem";
import { ADD_WAREHOUSE_ITEM_CONTAINER as addWarehouseItemContainerMutation } from "../../../../../../../graphql/mutation/WarehouseItem";
import { FETCH_WAREHOUSE_CATEGORIES } from "../../../../../../../graphql/query/WarehouseCategory";
import { FETCH_WAREHOUSE_SITES } from "../../../../../../../graphql/query/WarehouseSite";
import { FETCH_WAREHOUSE_LOCATIONS } from "../../../../../../../graphql/query/WarehouseLocation";
import { useSiteLocationOptions } from "../../../../../components/WarehouseSearch/components/siteLocations";
import { initialAddItemFilterValues, toAddItemFilter } from "./addItemFilter";

const FilterBar = props => {
  const { formik } = props || {};
  const { data: { warehouseCategories = [] } = {} } = useQuery(
    FETCH_WAREHOUSE_CATEGORIES,
  );
  const { data: { warehouseSites = [] } = {} } = useQuery(
    FETCH_WAREHOUSE_SITES,
  );
  const { data: { warehouseLocations = [] } = {} } = useQuery(
    FETCH_WAREHOUSE_LOCATIONS,
    { variables: { input: { siteId: { value: 0, operator: "gt" } } } },
  );
  const locationOptions = useSiteLocationOptions({
    formik,
    locationFieldName: "warehouseMoveLocationId",
    locations: warehouseLocations,
    siteFieldName: "warehouseMoveSiteId",
  });

  const inputs = {
    className:
      "grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-[100px_minmax(0,1fr)_minmax(0,1.5fr)_120px_minmax(0,1fr)_minmax(0,1fr)_auto]",
    inputs: [
      {
        label: "Storage",
        name: "id",
        type: "text",
      },
      {
        label: "Category",
        name: "categoryId",
        type: "multi-select",
        options: warehouseCategories?.map(item => ({
          label: item.description,
          value: item.id,
        })),
      },
      {
        label: "Description",
        name: "description",
        type: "long-text",
      },
      {
        label: "External Ref",
        name: "externalRef",
        type: "text",
      },
      {
        label: "Site",
        name: "warehouseMoveSiteId",
        type: "multi-select",
        isNullable: true,
        options: warehouseSites.map(site => ({
          label: site.name,
          value: site.id,
        })),
      },
      {
        label: "Location",
        name: "warehouseMoveLocationId",
        type: "multi-select",
        isNullable: true,
        disabled: !!formik.values._unlocated,
        options: locationOptions,
      },
      {
        label: "Unlocated",
        name: "_unlocated",
        type: "toggle",
        designType: "checkbox",
      },
    ],
  };

  const inputProps = {
    formik,
    ...inputs,
  };

  return <GridInputs {...inputProps} />;
};

const AddItemModal = props => {
  const { closeModal, warehouseItem, warehouseItemId } = props;
  const [
    addWarehouseItemContainer,
    {
      data: { addWarehouseItemContainer: { error } = {} } = {},
      loading,
      reset,
    },
  ] = useMutation(addWarehouseItemContainerMutation(), {
    refetchQueries: ["FetchWarehouseItemsV2"],
  });

  const formik = useFormik({
    initialValues: {},
  });

  const filterFormik = useFormik({
    initialValues: initialAddItemFilterValues,
    validationSchema: Yup.object({}),
  });

  const selectedItems = Object.keys(
    formik?.values?.selectedWarehouseItems?.ids || {},
  )?.map(item => parseInt(item.replace("_", "")));

  useEffect(() => {
    reset();
  }, [formik?.values?.selectedWarehouseItems]);

  const multiSelectProps = {
    formik,
    label: "Warehouse Items",
    name: "selectedWarehouseItems",
    keyName: "id",
    showAll: false,
  };

  const resetAll = () => {
    formik.resetForm();
    reset();
    filterFormik.resetForm();
    closeModal();
  };

  const tableProps = {
    allowHorizontalScroll: true,
    dataKey: "warehouseItemsV2",
    FETCH_QUERY: fetchWarehouseItemsV2({
      // WarehouseItem scalar fields
      mgroup: true,
      description: true,
      instanceId: true,
      externalRef: true,
      // WarehouseItem associations
      category: {
        description: true,
      },
      artist: {
        name: true,
      },
      instance: {
        edition: {
          year: true,
          artwork: {
            title: true,
            images: false,
            artworkCategory: false,
            instanceReference: false,
          },
        },
      },
    }),
    headers: [
      multiSelect(multiSelectProps),
      { label: "Storage#", name: "id", type: "label" },
      { label: "Category", name: "category.description", type: "label" },
      { label: "Group", name: "mgroup", type: "label" },
      { label: "Description", name: "description", type: "label" },
      { label: "Instance", name: "instanceId", type: "label" },
      { label: "Title", name: "instance.edition.artwork.title", type: "label" },
      { label: "Year", name: "instance.edition.year", type: "label" },
      { label: "Artist", name: "artist.name", type: "label" },
      { label: "External Ref", name: "externalRef", type: "label" },
    ],
    inputs: [],
    limit: 10,
    pagination: {
      variant: "sm",
    },
    variant: "dynamicHeight",
    variables: {
      idToSkip: {
        operator: "ne",
        value: warehouseItemId,
      },
      ...toAddItemFilter(filterFormik.values),
    },
    skip: () => !filterFormik?.isValid,
  };

  const handleAddItem = () => {
    addWarehouseItemContainer({
      variables: {
        input: {
          container: warehouseItem?.id,
          items: selectedItems,
        },
      },
    }).then(resp => {
      const { data: { addWarehouseItemContainer: { success } = {} } = {} } =
        resp;

      if (success) {
        formik?.resetForm();
        reset();
        closeModal();
      }
    });
  };

  return (
    <div className="mt-6 flex w-full flex-col">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col">
          <div className="text-2xl font-bold">Add Warehouse Item(s)</div>
          <p className="mt-4">
            {selectedItems?.length} Selected
            <span className="block text-red-500 sm:ml-8 sm:inline">
              {error}
            </span>
          </p>
        </div>
        <div className="flex flex-col">
          <div>
            <Button
              action="default"
              className="mr-4"
              label="Clear Filters"
              onClick={() => {
                filterFormik.resetForm();
              }}
            />
            <Button
              action="default"
              className="mr-4"
              label="Cancel"
              onClick={() => {
                resetAll();
              }}
            />
            <Button
              label={loading ? "Adding" : "Add"}
              disabled={loading || !selectedItems?.length}
              onClick={handleAddItem}
            />
          </div>
        </div>
      </div>
      <div className="my-4">
        <FilterBar formik={filterFormik} />
      </div>
      <Table {...tableProps} />
    </div>
  );
};

const AddItem = props => {
  const { warehouseItem, warehouseItemId } = props || {};

  const modalProps = {
    body: AddItemModal,
    closeOnBackdrop: false,
    warehouseItem,
    hideCloseButton: false,
    scale: "lg",
    warehouseItemId,
  };

  return (
    <Modal {...modalProps}>
      <Button label="Add Item" action="black" />
    </Modal>
  );
};

export default AddItem;
